import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

const BOTTOM_MARGIN_PX = 24;

/**
 * Pixel height for a pane that should fill whatever is left of the window below wherever it happens
 * to start, keeping a small breathing margin at the bottom.
 *
 * Measured rather than expressed in CSS (`calc(100vh - …)`) because the pane's own top offset is not
 * knowable in CSS — it depends on however much chrome the host page renders above it.
 *
 * Shared rather than kept inside `FileManager.vue` because the pane-level status notices moved out to
 * the hosts (D4): a host renders a notice *instead of* the file manager, and `FileManagerNotice`
 * centres itself in `height: 100%`, so the host's notice wrapper has to be the same box the browsable
 * pane would have been. The element ref is watched, not only measured on mount, precisely because a
 * host toggles that wrapper in and out with `v-if`.
 */
export const usePaneHeight = () => {
  const paneRef = ref<HTMLElement | null>(null);
  const paneHeight = ref(0);

  const updatePaneHeight = () => {
    if (!paneRef.value) return;
    const top = paneRef.value.getBoundingClientRect().top + window.scrollY;
    paneHeight.value = Math.max(window.innerHeight - top - BOTTOM_MARGIN_PX, 0);
  };

  // flush: 'post' so the element a `v-if` just created is already in the document when measured.
  watch(paneRef, updatePaneHeight, { flush: 'post' });

  onMounted(() => {
    updatePaneHeight();
    window.addEventListener('resize', updatePaneHeight);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', updatePaneHeight);
  });

  return { paneRef, paneHeight, updatePaneHeight };
};
